/*
 * Bundle Builder - AU compression socks only
 * Loaded conditionally from sections/main-product.liquid when:
 *   localization.market.handle == 'au'
 *   AND product is in the `compression-socks` collection
 *
 * Pairs with: snippets/bundle-builder-au.liquid + assets/bundle-builder-au.css
 *
 * Mirrors the custom-element + /cart/add.js + PUB_SUB_EVENTS.cartUpdate
 * pattern used in assets/product-form.js.
 */
if (!customElements.get('bundle-builder-au')) {
  // Back-in-stock sign-ups go to Pareto Hub's back-in-stock form (its flow
  // emails when the size is back), found through the Pareto forms script
  // (layout/theme.liquid).

  customElements.define(
    'bundle-builder-au',
    class BundleBuilderAU extends HTMLElement {
      constructor() {
        super();
        this.initialized = false;
      }

      connectedCallback() {
        if (this.initialized) return;
        try {
          this.init();
          this.initialized = true;
        } catch (err) {
          console.error('[bundle-builder-au] init failed', err);
        }
      }

      /* ---------- Init ---------- */

      init() {
        const dataScript = this.querySelector('script[data-bb-data]');
        if (!dataScript) throw new Error('missing data script');
        this.data = JSON.parse(dataScript.textContent);

        // Elements
        this.packButtons = Array.from(this.querySelectorAll('[data-bb-pack]'));
        this.sizeButtons = Array.from(this.querySelectorAll('[data-bb-size]'));
        this.styleButtons = Array.from(this.querySelectorAll('[data-bb-style]'));
        this.slotsEl = this.querySelector('[data-bb-slots]');
        this.atcBtn = this.querySelector('[data-bb-atc]');
        this.atcLabel = this.querySelector('[data-bb-atc-label]');
        this.errorEl = this.querySelector('[data-bb-error]');
        this.nudgeEl = this.querySelector('[data-bb-nudge]');
        this.preorderEl = this.querySelector('[data-bb-preorder]');
        this.preorderNoteEl = this.querySelector('[data-bb-preorder-note]');

        // State
        this.packTier = 3;

        // Default size: first button in the fixed list that the *current*
        // product has as an available variant. Falls back to the first
        // button if the product has no matching size (the seed slot will
        // then render as unavailable and prompt the shopper to change size).
        const currentVariants = (this.data.currentProduct && this.data.currentProduct.variants) || {};
        let defaultSize = '';
        for (const btn of this.sizeButtons) {
          const code = btn.dataset.bbSize;
          const entry = currentVariants[code];
          if (entry && entry.available !== false) {
            defaultSize = code;
            break;
          }
        }
        if (!defaultSize && this.sizeButtons[0]) {
          defaultSize = this.sizeButtons[0].dataset.bbSize || '';
        }
        this.sizeCode = defaultSize;

        // Sync button selected state to the resolved default.
        this.sizeButtons.forEach((btn) => {
          const isSelected = btn.dataset.bbSize === this.sizeCode;
          btn.classList.toggle('is-selected', isSelected);
          btn.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });

        this.slots = []; // Array<Slot>

        // Start with NOTHING selected - the shopper must click a style to add
        // a pair. (We used to seed slot 1 with the current product; that made
        // a pair appear auto-selected, which we no longer want.)

        // Event wiring
        this.packButtons.forEach((btn) =>
          btn.addEventListener('click', () => this.onPackClick(btn))
        );
        this.sizeButtons.forEach((btn) =>
          btn.addEventListener('click', () => this.onSizeClick(btn))
        );
        this.styleButtons.forEach((btn) => {
          btn.addEventListener('click', (e) => this.onStyleClick(btn, e));

          // Wire the "remove all of this style" X overlay directly so it
          // runs before the button's click handler and cannot be swallowed
          // by any DOM stacking / hit-testing edge cases.
          const xBtn = btn.querySelector('[data-bb-style-remove]');
          if (xBtn) {
            const removeHandler = (e) => {
              e.stopPropagation();
              e.preventDefault();
              const pid = parseInt(btn.dataset.productId, 10);
              this.removeAllOfStyle(pid);
            };
            xBtn.addEventListener('click', removeHandler);
            // Touch devices fire pointerdown before click - prevent the
            // outer button from starting its own selection gesture.
            xBtn.addEventListener('pointerdown', (e) => e.stopPropagation());
          }
        });
        this.slotsEl.addEventListener('click', (e) => {
          const rm = e.target.closest('[data-bb-remove]');
          if (rm) this.onRemoveSlot(parseInt(rm.dataset.bbRemove, 10));
        });
        this.atcBtn.addEventListener('click', () => this.onAddToCart());

        // Size guide popup - intercept the link click and show an in-page modal
        const sizeGuideLink = this.querySelector('.bb-size-guide');
        if (sizeGuideLink) {
          sizeGuideLink.addEventListener('click', (e) => {
            e.preventDefault();
            this.openSizeGuideModal(sizeGuideLink.href);
          });
        }

        // Cart drawer reference (for sections-render optimisation)
        this.cart =
          document.querySelector('cart-drawer') ||
          document.querySelector('cart-notification');
        if (this.cart) this.atcBtn.setAttribute('aria-haspopup', 'dialog');

        // Hide legacy picker / size / style-swatch / buy-button blocks that
        // live next to the bundle builder on compression sock PDPs. The
        // bundle builder replaces them wholesale.
        this.hideLegacyBlocks();

        // Intercept Shop Pay / Afterpay / PayPal / Google Pay dynamic
        // checkout clicks so the full bundle gets added to cart first.
        this.setupDynamicCheckoutIntercept();

        this.setupFilters();
        this.setupNotify();

        // Initial render
        this.renderPackCards();
        this.renderSlots();
        this.renderStyles();
        this.renderAtc();
        this.renderNudge();
      }

      /* ---------- Legacy block hiding ---------- */

      hideLegacyBlocks() {
        const parent = this.parentElement;
        if (!parent) return;

        // Wholesale-hide these legacy blocks (none contain the payment button)
        const legacySelectors = [
          '.sock-style-selector',
          '.sock-style-selected-name',
          '.variants-size-box',
          'variant-radios',
          'variant-selects',
          'quantity-input',
        ];
        legacySelectors.forEach((sel) => {
          parent.querySelectorAll(sel).forEach((el) => {
            el.style.setProperty('display', 'none', 'important');
          });
        });

        // The theme renders the buy buttons inside:
        //   .buy--buttons > product-form.product-form > form > .product-form__buttons
        //     └─ button[name="add"]  (hide - bundle builder has its own ATC)
        //     └─ .shopify-payment-button  (KEEP - these are the dynamic
        //        checkout buttons: Shop Pay, Afterpay, PayPal, etc.)
        //
        // We cannot detach the payment button from its form without breaking
        // it (Shopify's script owns the form reference). So: keep the form
        // intact, hide only the bits we don't want, and relocate the whole
        // <product-form> (or buy--buttons wrapper) to sit directly below our
        // bundle-builder ATC.
        try {
          const productForm =
            parent.querySelector('product-form.product-form') ||
            parent.querySelector('.product-form') ||
            document.querySelector('.product__info-container product-form.product-form');
          if (productForm) {
            // Hide the inline ATC button, the error wrapper, the pre-order note
            // (the builder shows its own under its ATC) and the back-in-stock
            // line (the builder's notify popup says it per style)
            productForm
              .querySelectorAll('button[name="add"], .product-form__error-message-wrapper, [data-preorder-note], [data-restock-note]')
              .forEach((el) => el.style.setProperty('display', 'none', 'important'));

            // The theme renders the hidden `name="id"` input with `disabled`;
            // the theme's variant picker normally flips that off when a
            // variant is chosen. We've hidden the variant picker, so the
            // input stays disabled and the Shopify dynamic checkout script
            // renders its buttons with zero height. Enable the input and
            // point it at the current product's first available variant so
            // Shop Pay / Afterpay / PayPal / Google Pay render and work.
            const variantIdInput = productForm.querySelector('input[name="id"]');
            if (variantIdInput) {
              variantIdInput.removeAttribute('disabled');
              const currentVariants = (this.data.currentProduct && this.data.currentProduct.variants) || {};
              let seedVariantId = '';
              for (const code in currentVariants) {
                const entry = currentVariants[code];
                if (entry && entry.available !== false && entry.id) {
                  seedVariantId = String(entry.id);
                  break;
                }
              }
              if (seedVariantId) {
                variantIdInput.value = seedVariantId;
                variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }

            // Move the form (with its payment button still inside) to appear
            // directly under our ATC, wrapped in a container we can style.
            const wrap =
              this.parentElement.querySelector('.bb-dynamic-checkout') ||
              document.createElement('div');
            wrap.className = 'bb-dynamic-checkout';
            if (!wrap.isConnected) {
              // Place the Shop Pay / dynamic-checkout buttons AFTER the micro
              // reassurance hint so the hint sits directly under the green ATC
              // (matches the tw1 reference ordering).
              const hint = this.querySelector('.bb-atc-hint');
              if (hint) {
                hint.insertAdjacentElement('afterend', wrap);
              } else {
                this.atcBtn.insertAdjacentElement('afterend', wrap);
              }
            }
            wrap.appendChild(productForm);
          }
        } catch (err) {
          console.warn('[bundle-builder-au] could not relocate product form', err);
        }

        // Hide the buy--buttons wrapper IF it's now empty (the product form
        // we cared about was moved out). Leave it alone if it still has
        // content we didn't touch.
        parent.querySelectorAll('.buy--buttons').forEach((el) => {
          if (el.querySelector('product-form, .shopify-payment-button')) return;
          el.style.setProperty('display', 'none', 'important');
        });

        // Hide the bare "<b>Select Style:</b>" label that sits directly
        // before the sock-style-selected-name span. It has no class so we
        // locate it via the adjacent sibling.
        Array.from(parent.children).forEach((child) => {
          if (
            child.tagName === 'B' &&
            child.nextElementSibling &&
            child.nextElementSibling.classList &&
            child.nextElementSibling.classList.contains('sock-style-selected-name')
          ) {
            child.style.setProperty('display', 'none', 'important');
          }
        });

        // Also hide any HR immediately preceding the bundle builder that
        // was acting as a divider before the legacy picker, so we don't
        // leave a stray line above our UI.
        const prev = this.previousElementSibling;
        if (prev && prev.tagName === 'HR') {
          prev.style.setProperty('display', 'none', 'important');
        }
      }

      /* ---------- Slot helpers ---------- */

      getStyleDataFromButton(btn) {
        let variants = {};
        try {
          variants = JSON.parse(btn.dataset.variants || '{}');
        } catch (_) {
          variants = {};
        }
        let images = [];
        try {
          images = JSON.parse(btn.dataset.images || '[]');
        } catch (_) {
          images = [];
        }
        // Normalise to the same shape as currentProduct.variants
        const prod = {
          id: parseInt(btn.dataset.productId, 10),
          handle: btn.dataset.productHandle,
          title: btn.dataset.productTitle,
          url: btn.dataset.productUrl,
          hero: btn.dataset.hero,
          thumb: btn.dataset.thumb,
          images, // [{ l: "<1500w url>", s: "<200w url>", a: "<alt>" }, …]
          variants,
        };
        return prod;
      }

      updateBasePriceForCurrentContext() {
        // Look up the price for the current size from the currently-displayed
        // product. Check style buttons for the last-selected style first,
        // then fall back to the initial currentProduct data.
        const lastSlot = this.slots.length ? this.slots[this.slots.length - 1] : null;
        let variants = null;
        if (lastSlot) {
          const btn = this.styleButtons.find(
            (b) => parseInt(b.dataset.productId, 10) === lastSlot.productId
          );
          if (btn) {
            try { variants = JSON.parse(btn.dataset.variants || '{}'); } catch (_) {}
          }
        }
        if (!variants && this.data.currentProduct) {
          variants = this.data.currentProduct.variants;
        }
        if (variants) {
          const entry = variants[this.sizeCode];
          if (entry && entry.price) {
            this.data.basePriceCents = entry.price;
          }
        }
      }

      /* ---------- Event handlers ---------- */

      onPackClick(btn) {
        const qty = parseInt(btn.dataset.bbPack, 10);
        if (!qty || qty === this.packTier) return;
        this.packTier = qty;

        // Shrink: keep first N. Grow: no immediate change, just allow more picks.
        if (this.slots.length > qty && qty < 4) {
          this.slots = this.slots.slice(0, qty);
        }

        this.renderPackCards();
        this.renderSlots();
        this.renderStyles();
        this.renderAtc();
        this.renderNudge();
      }

      onSizeClick(btn) {
        const next = btn.dataset.bbSize;
        if (!next || next === this.sizeCode) return;

        // Size selector is the "size for the next pick" - it does NOT
        // retroactively change the size of items already in the bundle.
        // Each slot carries its own sizeCode (set at the time of adding).
        this.sizeCode = next;

        // The open back-in-stock panel is for a variant in the old size.
        this.closeNotify(false);

        // Update basePriceCents for the new size - check the last-selected
        // style (or current product) for a price at this size.
        this.updateBasePriceForCurrentContext();

        this.renderSizeButtons();
        this.renderPackCards();
        this.renderStyles(); // re-filter the style grid for the new next-pick size
        this.renderAtc();
        this.renderNudge();
      }

      onStyleClick(btn, e) {
        // If the click landed on the "remove all" X overlay, remove every
        // slot belonging to this style and bail out (do NOT add another).
        if (e && e.target && e.target.closest('[data-bb-style-remove]')) {
          e.stopPropagation();
          e.preventDefault();
          const pid = parseInt(btn.dataset.productId, 10);
          this.removeAllOfStyle(pid);
          return;
        }

        // Sold out in this size but made in it: offer the back-in-stock
        // signup instead of doing nothing.
        if (btn.classList.contains('is-notify')) {
          this.openNotify(btn);
          return;
        }

        if (
          btn.classList.contains('is-oos') ||
          btn.classList.contains('is-oos-size') ||
          btn.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }
        const prod = this.getStyleDataFromButton(btn);
        const entry = prod.variants[this.sizeCode];
        if (!entry || entry.available === false) {
          this.showError('This style is sold out at the selected size.');
          return;
        }
        this.clearError();

        const nextSlot = {
          productId: prod.id,
          productHandle: prod.handle,
          variantId: entry.id,
          available: true,
          title: prod.title,
          heroUrl: prod.hero,
          thumbUrl: prod.thumb,
          sizeCode: this.sizeCode,
          // Pareto Hub pre-order plan for this size ({ plan, label, date }),
          // or null. Sent as `selling_plan` with the pair; see cartItems().
          preorder: entry.preorder || null,
        };

        // Update basePriceCents to reflect the newly selected style's price
        // so the pack cards show accurate pricing for this product.
        if (entry.price) {
          this.data.basePriceCents = entry.price;
        }

        // Capacity logic:
        // - At tiers 1/2/3, if the bundle is already full, auto-upgrade the
        //   pack tier to accommodate the new pick (e.g. 2-pack with 2 items
        //   + new click → bump to 3-pack). Cap at 4; past 4 we're in
        //   uncapped "4+" mode and just append.
        // - At tier 4 the bundle is uncapped.
        const filled = this.slots.length;
        if (this.packTier < 4 && filled >= this.packTier) {
          this.packTier = Math.min(4, filled + 1);
        }

        // Did this pick earn a better rate than the one before it did?
        const earnedBefore = this.tierForCount(filled);
        this.slots.push(nextSlot);
        const earnedNow = this.tierForCount(this.slots.length);

        // Dynamic PDP title + hero update (req #2)
        this.updatePdpVisual(prod);

        this.renderPackCards();
        this.renderSlots();
        this.renderStyles();
        this.renderAtc();

        if ((earnedNow.discountPct || 0) > (earnedBefore.discountPct || 0)) {
          this.showUnlock(earnedNow);
        } else {
          this.renderNudge();
        }
      }

      onRemoveSlot(index) {
        if (Number.isNaN(index)) return;
        this.slots.splice(index, 1);
        this._unlockUntil = 0;
        this.renderPackCards();
        this.renderSlots();
        this.renderStyles();
        this.renderAtc();
        this.renderNudge();
      }

      removeAllOfStyle(productId) {
        if (!productId) return;
        const before = this.slots.length;
        this.slots = this.slots.filter((s) => s.productId !== productId);
        if (this.slots.length === before) return;
        this._unlockUntil = 0;
        this.renderPackCards();
        this.renderSlots();
        this.renderStyles();
        this.renderAtc();
        this.renderNudge();
      }

      /* ---------- PDP cosmetic update ---------- */

      updatePdpVisual(prod) {
        // Title - the main-product section's `title` block renders an h1 inside .product__title
        const titleEl =
          document.querySelector('.product__info-wrapper .product__title h1') ||
          document.querySelector('.product__title h1') ||
          document.querySelector('h1.product__title') ||
          document.querySelector('h1');
        if (titleEl && prod.title) titleEl.textContent = prod.title;

        // Resolve image list for this style. Fall back to [{l,s,a}] built
        // from the single hero/thumb URLs if the detailed images array is
        // missing (e.g. the current product which only has hero/thumb).
        let images = Array.isArray(prod.images) ? prod.images.slice() : [];
        if (!images.length && prod.hero) {
          images = [{ l: prod.hero, s: prod.thumb || prod.hero, a: prod.title || '' }];
        }

        // --- Main slides ---
        const slides = Array.from(document.querySelectorAll('.product__media-list .product__media-item'));
        slides.forEach((slide, i) => {
          const data = images[i];
          if (data) {
            // Update every <img> inside this slide (there can be several -
            // the main `img`, the `image-magnify-lightbox` img used by the
            // hover-zoom component, etc.). Swapping them all ensures the
            // expanded/zoomed view shows the new style, not the old.
            slide.querySelectorAll('img').forEach((img) => {
              img.setAttribute('src', data.l);
              img.removeAttribute('srcset');
              img.removeAttribute('data-srcset');
              if (data.a) img.setAttribute('alt', data.a);
            });
            slide.style.removeProperty('display');
          } else {
            // No corresponding image for this slot - hide the slide.
            slide.style.setProperty('display', 'none', 'important');
          }
        });

        // --- Expanded / lightbox view (product-modal) ---
        // The theme renders a <product-modal> with one slide per media-id.
        // Shoppers click the hero to open it. Swap the modal images too so
        // the lightbox reflects the newly-selected style.
        const modalSlides = Array.from(document.querySelectorAll('product-modal .product-media-modal__content, .product-media-modal__content'));
        modalSlides.forEach((node, i) => {
          const data = images[i];
          if (data) {
            node.querySelectorAll('img').forEach((img) => {
              img.setAttribute('src', data.l);
              img.removeAttribute('srcset');
              img.removeAttribute('data-srcset');
              if (data.a) img.setAttribute('alt', data.a);
            });
            node.style.removeProperty('display');
          } else {
            node.style.setProperty('display', 'none', 'important');
          }
        });

        // --- Thumbnail slider ---
        const thumbs = Array.from(document.querySelectorAll('.thumbnail-slider .thumbnail-list__item, .thumbnail-list .thumbnail-list__item'));
        thumbs.forEach((thumb, i) => {
          const img = thumb.querySelector('img');
          const data = images[i];
          if (data && img) {
            img.setAttribute('src', data.s);
            img.removeAttribute('srcset');
            img.removeAttribute('data-srcset');
            if (data.a) img.setAttribute('alt', data.a);
            thumb.style.removeProperty('display');
          } else {
            thumb.style.setProperty('display', 'none', 'important');
          }
        });

        // Snap the main slider back to the first slide (active state) so
        // shoppers see the new hero without needing to scroll the gallery.
        const firstSlide = slides[0];
        if (firstSlide) {
          slides.forEach((s) => s.classList.remove('is-active'));
          firstSlide.classList.add('is-active');
          const slider =
            firstSlide.closest('slider-component') ||
            firstSlide.closest('.product__media-list');
          if (slider && typeof slider.setActivePage === 'function') {
            try { slider.setActivePage(0); } catch (_) {}
          } else if (slider && slider.scrollTo) {
            try { slider.scrollTo({ left: 0, behavior: 'instant' }); } catch (_) {}
          }
        }
      }

      /* ---------- Pricing ---------- */

      // Shopify truncates the per-line discount to whole cents, so mirror that
      // rather than rounding the discounted unit price. At 15% off $34.97 the
      // rounding form gave $29.72/pair ($89.16 for three) while the cart
      // actually charges $29.73 ($89.19). Verified against the live cart at
      // qty 1/2/3 on 2026-08-22.
      unitPriceCents(basePriceCents, discountPct) {
        const base = Number(basePriceCents) || 0;
        const pct = Number(discountPct) || 0;
        return base - Math.floor((base * pct) / 100);
      }

      // The tier a given number of pairs actually earns. This is keyed off the
      // real pair count, NOT this.packTier: the pack tile is a display/capacity
      // hint that defaults to 3, so pricing off it quoted a single pair at the
      // 3-pack discount ($29.72 against the $34.97 the cart charges).
      tierForCount(count) {
        const tiers = (this.data.packTiers || []).slice().sort((a, b) => a.qty - b.qty);
        let match = tiers[0] || { qty: 1, discountPct: 0, perk: '' };
        tiers.forEach((t) => {
          if (count >= t.qty) match = t;
        });
        return match;
      }

      nextTierForCount(count) {
        const tiers = (this.data.packTiers || []).slice().sort((a, b) => a.qty - b.qty);
        return tiers.find((t) => t.qty > count && (t.discountPct || 0) > 0) || null;
      }

      // What the ladder itself saves, i.e. against paying the single-pair price
      // for the same number of pairs. Deliberately NOT measured against the
      // compare-at price: the nudge below quotes the marginal saving of one
      // more pair, and only this basis makes the two numbers add up.
      bundleSavingCents(count) {
        const base = Number(this.data.basePriceCents) || 0;
        if (!count) return 0;
        const tier = this.tierForCount(count);
        return base * count - this.unitPriceCents(base, tier.discountPct) * count;
      }

      bundleTotalCents(count) {
        const base = Number(this.data.basePriceCents) || 0;
        const tier = this.tierForCount(count);
        return this.unitPriceCents(base, tier.discountPct) * count;
      }

      /* ---------- Rendering ---------- */

      renderPackCards() {
        const basePriceCents = Number(this.data.basePriceCents) || 0;
        const format = (cents) => this.formatMoney(cents);

        this.packButtons.forEach((btn) => {
          const qty = parseInt(btn.dataset.bbPack, 10);
          const discountPct = parseFloat(btn.dataset.bbDiscount) || 0;
          const perPair = this.unitPriceCents(basePriceCents, discountPct);
          const total = perPair * qty;

          const totalEl = btn.querySelector('[data-bb-pack-total]');
          const unitEl = btn.querySelector('[data-bb-pack-unit]');
          if (totalEl) totalEl.textContent = format(total);
          if (unitEl) {
            // 1-Pack shows the per-pair price; multi-packs show the extra saving
            // (marketing figures set on the tile, not the raw discount %) —
            // matches the tw1 reference.
            const saveDisplay = btn.dataset.bbSaveDisplay;
            if (saveDisplay) {
              unitEl.textContent = 'Save Extra ' + saveDisplay + '%';
            } else {
              unitEl.textContent = format(perPair) + '/pair';
            }
          }

          const isSelected = qty === this.packTier;
          btn.classList.toggle('is-selected', isSelected);
          btn.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });
      }

      renderSizeButtons() {
        this.sizeButtons.forEach((btn) => {
          const isSelected = btn.dataset.bbSize === this.sizeCode;
          btn.classList.toggle('is-selected', isSelected);
          btn.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });
      }

      renderStyles() {
        const selectedIds = new Set(this.slots.map((s) => s.productId));
        let notifyCount = 0;
        this.styleButtons.forEach((btn) => {
          const pid = parseInt(btn.dataset.productId, 10);
          const isInBundle = selectedIds.has(pid);
          btn.classList.toggle('is-selected', isInBundle);
          // `is-current` is baked in by Liquid for the product whose page
          // we're on. Once the shopper removes it from the bundle it must
          // no longer show the "in bundle" tick - so treat is-current as
          // a mirror of is-selected from here on.
          btn.classList.toggle('is-current', isInBundle && pid === this.data.currentProduct.id);

          // Mark as "sold out at current size" if the style has no variant
          // for this size, or the variant is unavailable. Keep the original
          // `.is-oos` class (fully OOS across all sizes) untouched - it's
          // applied server-side in Liquid.
          let oosForSize = true;
          let lowForSize = false;
          let notify = false;
          let preorder = null;
          let back = '';
          try {
            const variants = JSON.parse(btn.dataset.variants || '{}');
            const entry = variants[this.sizeCode];
            oosForSize = !entry || entry.available === false;
            // Back-in-stock needs a real variant: a style that is not made in
            // this size at all stays a plain "Sold out" tile.
            notify = !!(this.notifyEl && entry && entry.id && entry.available === false);
            // Scarcity is per size: a style can hold hundreds of units overall
            // and still be down to the last few in the size being bought.
            lowForSize = !oosForSize && !!(entry && entry.low);
            // On pre-order (Pareto Hub): out of stock with more on the way, and
            // still selectable. The Liquid only sends `preorder` while the
            // variant is available, so a full pre-order reads sold out (bell).
            preorder = (!oosForSize && entry && entry.preorder) || null;
            // Pareto Hub back-in-stock date for a sold-out size ("21 Nov").
            back = (oosForSize && entry && entry.back) || '';
          } catch (_) {
            oosForSize = true;
          }
          btn.classList.toggle('is-low', lowForSize);
          btn.classList.toggle('is-oos-size', oosForSize);
          btn.classList.toggle('is-notify', notify);
          btn.classList.toggle('is-preorder', !!preorder);
          const unavailable = oosForSize || btn.classList.contains('is-oos');
          if (unavailable && !notify) {
            btn.setAttribute('aria-disabled', 'true');
          } else {
            btn.removeAttribute('aria-disabled');
          }
          const title = btn.dataset.productTitle || '';
          btn.title = back ? title + ': back in stock around ' + back : title;
          if (notify) {
            notifyCount += 1;
            btn.setAttribute(
              'aria-label',
              title + ', sold out in ' + this.sizeCode + (back ? ', back in stock around ' + back : '') + '. Notify me when it\'s back'
            );
            btn.setAttribute('aria-controls', this.notifyEl.id);
            btn.setAttribute(
              'aria-expanded',
              this.notifyTarget && this.notifyTarget.btn === btn ? 'true' : 'false'
            );
          } else {
            btn.setAttribute('aria-label', preorder ? title + ', ' + this.sizeCode + '. ' + preorder.label : title);
            btn.removeAttribute('aria-controls');
            btn.removeAttribute('aria-expanded');
          }

          // Keep what the shopper can actually buy at the front of the grid.
          // Matters most in the big sizes, where only a handful of the 40-odd
          // styles are stocked and they would otherwise be scattered.
          const cell = btn.closest('.bb-style-cell');
          if (cell) {
            const base = parseInt(cell.dataset.bbBaseOrder || '50', 10);
            cell.style.order = String(unavailable ? base + 100 : base);
          }
        });

        if (this.notifyHintEl) {
          this.notifyHintEl.hidden = notifyCount === 0;
          const hintText = this.notifyHintEl.querySelector('[data-bb-notify-hint-text]');
          if (hintText && notifyCount) {
            hintText.textContent =
              'Sold out in ' + this.sizeCode + '? Pick a style with a bell to get an email when it\'s back.';
          }
        }
      }

      renderSlots() {
        // Capacity: at tiers 1-3 = tier; at tier 4+ = max(4, slots.length)
        const capacity =
          this.packTier < 4 ? this.packTier : Math.max(4, this.slots.length);

        const frag = document.createDocumentFragment();
        for (let i = 0; i < capacity; i++) {
          const slot = this.slots[i];
          const li = document.createElement('li');
          li.className = 'bb-slot ' + (slot ? 'is-filled' : 'is-empty');
          if (slot && slot.available === false) li.classList.add('is-warning');
          if (slot && slot.preorder) {
            li.classList.add('is-preorder');
            li.title = slot.preorder.label;
          }

          if (slot) {
            const img = document.createElement('img');
            img.src = slot.thumbUrl;
            img.alt = slot.title || '';
            img.loading = 'lazy';
            li.appendChild(img);

            const rm = document.createElement('button');
            rm.type = 'button';
            rm.className = 'bb-slot__remove';
            rm.setAttribute('aria-label', 'Remove ' + (slot.title || 'item') + ' from bundle');
            rm.dataset.bbRemove = String(i);
            rm.textContent = '×';
            li.appendChild(rm);

            if (slot.sizeCode) {
              const sizeBar = document.createElement('span');
              sizeBar.className = 'bb-slot__size';
              sizeBar.textContent = slot.sizeCode;
              li.appendChild(sizeBar);
            }
          } else {
            li.textContent = '+';
          }
          frag.appendChild(li);
        }
        this.slotsEl.innerHTML = '';
        this.slotsEl.appendChild(frag);
        this.renderPreorder();
      }

      /* ---------- Pre-order (Pareto Hub) ---------- */

      // Pre-order pairs, grouped by plan: the plan name ("Pre-order: ships by
      // 21 Nov") over the styles and sizes it covers. The note under the ATC
      // shows while the bundle has any.
      renderPreorder() {
        const groups = new Map(); // plan id -> { label, lines: Map(key -> { text, qty }) }
        this.slots.forEach((s) => {
          if (!s.preorder) return;
          const planKey = String(s.preorder.plan);
          if (!groups.has(planKey)) groups.set(planKey, { label: s.preorder.label, lines: new Map() });
          const lines = groups.get(planKey).lines;
          const key = s.variantId;
          const line = lines.get(key) || { text: (s.title || '') + (s.sizeCode ? ', ' + s.sizeCode : ''), qty: 0 };
          line.qty += 1;
          lines.set(key, line);
        });

        if (this.preorderNoteEl) this.preorderNoteEl.hidden = groups.size === 0;
        if (!this.preorderEl) return;
        this.preorderEl.innerHTML = '';
        this.preorderEl.hidden = groups.size === 0;
        groups.forEach((group) => {
          const block = document.createElement('div');
          block.className = 'bb-preorder__group';
          const label = document.createElement('div');
          label.className = 'bb-preorder__label';
          label.textContent = group.label;
          block.appendChild(label);
          group.lines.forEach((line) => {
            const item = document.createElement('div');
            item.className = 'bb-preorder__item';
            item.textContent = line.text + (line.qty > 1 ? ' (x' + line.qty + ')' : '');
            block.appendChild(item);
          });
          this.preorderEl.appendChild(block);
        });
      }

      // /cart/add.js items for the bundle. A pre-order pair carries its plan
      // as `selling_plan`: without it Shopify does not hold the line for the
      // incoming stock and it ships (or fails to) like normal stock.
      cartItems() {
        return this.slots.map((s) => {
          const item = { id: s.variantId, quantity: 1 };
          if (s.preorder && s.preorder.plan) item.selling_plan = s.preorder.plan;
          return item;
        });
      }

      renderAtc() {
        const count = this.slots.length;
        const hasInvalid = this.slots.some((s) => !s.variantId || s.available === false);
        const disabled = count === 0 || hasInvalid;

        if (this.atcLabel) {
          if (count === 0) {
            this.atcLabel.textContent = 'Add to Cart';
          } else {
            // "Add N Pairs to Cart - $total, save $x" - the saving is what the
            // bundle ladder earns over paying single-pair price for the same
            // number of pairs, which is the number the nudge increments.
            const total = this.bundleTotalCents(count);
            const saving = this.bundleSavingCents(count);
            const noun = count === 1 ? 'Pair' : 'Pairs';
            let label = 'Add ' + count + ' ' + noun + ' to Cart - ' + this.formatMoney(total);
            if (saving > 0) label += ', save ' + this.formatMoney(saving);
            this.atcLabel.textContent = label;
          }
        }
        this.atcBtn.toggleAttribute('disabled', disabled);
        this.atcBtn.setAttribute('aria-disabled', disabled ? 'true' : 'false');

        if (hasInvalid) {
          this.showError('One or more items in your bundle are sold out at the selected size. Remove them or change size.');
        } else {
          this.clearError();
        }
      }

      /* ---------- Next-tier nudge / unlock reward ---------- */

      // The pack ladder upgrades itself as pairs are added (see onStyleClick),
      // which used to happen in complete silence. This surfaces both halves of
      // it: what the next tier is worth, and a moment of credit when one lands.
      renderNudge() {
        if (!this.nudgeEl) return;
        if (this._unlockUntil && Date.now() < this._unlockUntil) return;

        const count = this.slots.length;
        this.nudgeEl.classList.remove('is-unlock');

        if (count === 0) {
          this.nudgeEl.hidden = true;
          this.nudgeEl.textContent = '';
          return;
        }

        const next = this.nextTierForCount(count);
        if (!next) {
          const saving = this.bundleSavingCents(count);
          this.nudgeEl.hidden = saving <= 0;
          this.nudgeEl.classList.add('is-top');
          this.nudgeEl.textContent = saving > 0
            ? "Best value unlocked - you're saving " + this.formatMoney(saving) + ' on this bundle'
            : '';
          return;
        }

        this.nudgeEl.classList.remove('is-top');
        const needed = next.qty - count;
        const extra = this.bundleSavingCents(next.qty) - this.bundleSavingCents(count);
        const noun = needed === 1 ? 'pair' : 'pairs';
        let text = 'Add ' + needed + ' more ' + noun;
        if (next.perk) text += ' to unlock ' + next.perk;
        if (extra > 0) text += ' - save ' + this.formatMoney(extra) + ' more';
        this.nudgeEl.textContent = text;
        this.nudgeEl.hidden = false;
      }

      showUnlock(tier) {
        if (!this.nudgeEl || !tier) return;
        const saving = this.bundleSavingCents(this.slots.length);
        const perk = tier.perk || 'a better price';
        this.nudgeEl.classList.remove('is-top');
        this.nudgeEl.classList.add('is-unlock');
        this.nudgeEl.textContent = perk.charAt(0).toUpperCase() + perk.slice(1) +
          " unlocked - you're saving " + this.formatMoney(saving) + ' on this bundle';
        this.nudgeEl.hidden = false;

        // Hold the credit briefly, then go back to pointing at the next rung.
        this._unlockUntil = Date.now() + 4000;
        clearTimeout(this._unlockTimer);
        this._unlockTimer = setTimeout(() => {
          this._unlockUntil = 0;
          this.renderNudge();
        }, 4000);
      }

      /* ---------- Style grid filtering ---------- */

      setupFilters() {
        const bar = this.querySelector('[data-bb-filters]');
        this.stylesEl = this.querySelector('[data-bb-styles]');
        if (!bar || !this.stylesEl) return;

        this.filterButtons = Array.from(bar.querySelectorAll('[data-bb-filter]'));
        const cells = Array.from(this.stylesEl.querySelectorAll('[data-bb-cat]'));

        // Remember the merchandised order Liquid rendered, so renderStyles can
        // demote styles that are sold out in the chosen size and put them back
        // when the shopper picks a size that has them.
        cells.forEach((cell) => {
          cell.dataset.bbBaseOrder = cell.style.order || '50';
        });

        // Counts come from the rendered cells, so the chips stay honest if the
        // collection changes. A chip with nothing behind it is dropped.
        this.filterButtons.forEach((btn) => {
          const key = btn.dataset.bbFilter;
          const n = key === 'all' ? cells.length : cells.filter((c) => c.dataset.bbCat === key).length;
          const countEl = btn.querySelector('[data-bb-filter-count]');
          if (countEl) countEl.textContent = n ? ' ' + n : '';
          if (!n) btn.hidden = true;
          btn.addEventListener('click', () => this.onFilterClick(btn));
        });
      }

      onFilterClick(btn) {
        const key = btn.dataset.bbFilter || 'all';
        this.stylesEl.dataset.bbActiveFilter = key;
        this.filterButtons.forEach((b) => {
          const on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        // Jump the horizontal strip back to the start so the filtered set is
        // visible without scrolling on mobile.
        if (this.stylesEl.scrollLeft) this.stylesEl.scrollLeft = 0;
      }

      /* ---------- Add to Cart ---------- */

      async onAddToCart() {
        if (this.atcBtn.getAttribute('aria-disabled') === 'true') return;
        if (!this.slots.length) return;

        this.clearError();
        this.atcBtn.classList.add('is-loading');

        const items = this.cartItems();
        const body = { items };

        // Request updated cart sections inline (same pattern as product-form.js)
        if (this.cart && this.cart.tagName === 'CART-DRAWER') {
          body.sections = ['cart-drawer', 'cart-icon-bubble'];
          body.sections_url = window.location.pathname;
        }

        try {
          const res = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          });

          const json = await res.json();
          if (!res.ok || (json && json.status && json.description)) {
            throw new Error((json && json.description) || 'Failed to add bundle to cart');
          }

          // Notify rest of theme; cart drawer refresh
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'bundle-builder-au',
              cartData: json,
            });
          }

          const drawer = this.cart || document.querySelector('cart-drawer');
          if (drawer && drawer.tagName === 'CART-DRAWER') {
            if (json.sections && typeof drawer.renderContents === 'function') {
              drawer.renderContents(json);
            } else if (typeof drawer.open === 'function') {
              // Fallback: manually refresh sections and open
              try {
                const [drawerHtml, iconHtml] = await Promise.all([
                  fetch('/?section_id=cart-drawer').then((r) => r.text()),
                  fetch('/?section_id=cart-icon-bubble').then((r) => r.text()),
                ]);
                drawer.renderContents({
                  sections: { 'cart-drawer': drawerHtml, 'cart-icon-bubble': iconHtml },
                });
              } catch (_) {
                drawer.open();
              }
            }
          }

          // Reset the bundle builder back to its default state so the
          // shopper can build another bundle immediately.
          this.resetBundle();
        } catch (err) {
          console.error('[bundle-builder-au] add to cart failed', err);
          this.showError(err.message || 'Something went wrong adding your bundle.');
        } finally {
          this.atcBtn.classList.remove('is-loading');
        }
      }

      /* ---------- Reset bundle to initial state ---------- */

      resetBundle() {
        // Back to default 3 Pack, default size, single slot seeded with current product.
        this.packTier = 3;
        this.packButtons.forEach((btn) => {
          const isDefault = btn.dataset.bbPack === '3';
          btn.classList.toggle('is-selected', isDefault);
          btn.setAttribute('aria-checked', isDefault ? 'true' : 'false');
        });

        // Re-resolve default size (first in-stock size for current product)
        const currentVariants =
          (this.data.currentProduct && this.data.currentProduct.variants) || {};
        let defaultSize = '';
        for (const code in currentVariants) {
          const entry = currentVariants[code];
          if (entry && entry.available !== false) {
            defaultSize = code;
            break;
          }
        }
        if (!defaultSize && this.sizeButtons[0]) {
          defaultSize = this.sizeButtons[0].dataset.bbSize || '';
        }
        this.sizeCode = defaultSize;
        this.sizeButtons.forEach((btn) => {
          const isSelected = btn.dataset.bbSize === this.sizeCode;
          btn.classList.toggle('is-selected', isSelected);
          btn.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });

        // Reset slots to empty - nothing is auto-selected; the shopper picks
        // styles to build a new bundle.
        this.slots = [];

        // Restore PDP visual to the current product (in case style swaps happened)
        if (this.data.currentProduct) {
          this.updatePdpVisual(this.data.currentProduct);
        }

        this.closeNotify(false);
        this._unlockUntil = 0;
        clearTimeout(this._unlockTimer);
        this.renderPackCards();
        this.renderSlots();
        this.renderStyles();
        this.renderAtc();
        this.renderNudge();
      }

      /* ---------- Dynamic checkout (Shop Pay / Afterpay / PayPal) intercept ---------- */

      setupDynamicCheckoutIntercept() {
        const wrap = this.querySelector('.bb-dynamic-checkout');
        if (!wrap) return;
        this._dcBusy = false;

        // If the browser restores the page from bfcache after the shopper
        // navigates back from /checkout, reset the busy flag so the buttons
        // work again.
        window.addEventListener('pageshow', () => {
          this._dcBusy = false;
          clearTimeout(this._dcBusyTimer);
        });

        // The Shop Pay product-page wallet button (<shop-pay-wallet-button>)
        // and the "More payment options" link BOTH skip the cart and open a
        // direct PDP checkout using only the form's current variant. That
        // loses every other bundle slot. So we can't delegate - we have to
        // take over the flow: add the full bundle to the cart ourselves and
        // then navigate to /checkout (where Shop Pay / PayPal / Afterpay are
        // still offered as accelerated methods).
        const handler = async (e) => {
          if (this._dcBusy) return;
          if (!this.slots.length) return;

          // Only intercept clicks inside the Shopify payment-button container.
          const inPaymentBtn = e.target.closest('.shopify-payment-button');
          if (!inPaymentBtn) return;

          e.preventDefault();
          e.stopImmediatePropagation();
          e.stopPropagation();

          this._dcBusy = true;
          // Safety net - if navigation never happens (blocked, cancelled,
          // network error mid-redirect), don't leave the buttons permanently
          // dead. Clear the flag after 8s.
          clearTimeout(this._dcBusyTimer);
          this._dcBusyTimer = setTimeout(() => {
            this._dcBusy = false;
          }, 8000);

          this.clearError();

          try {
            const items = this.cartItems();
            const res = await fetch(
              (window.routes && window.routes.cart_add_url) || '/cart/add.js',
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                  'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({ items }),
              }
            );
            const json = await res.json().catch(() => ({}));
            if (!res.ok || (json && json.status && json.description)) {
              throw new Error(
                (json && json.description) || 'Failed to add bundle to cart'
              );
            }

            // Navigate to checkout. Shop Pay / Afterpay / PayPal / Google Pay
            // are offered there as accelerated checkout methods, and the full
            // bundle is now in the cart.
            window.location.href = '/checkout';
          } catch (err) {
            console.error('[bundle-builder-au] dynamic checkout add failed', err);
            this._dcBusy = false;
            clearTimeout(this._dcBusyTimer);
            this.showError(
              err.message || 'Could not add your bundle. Please try again.'
            );
          }
        };

        // Use capture phase so we beat any handlers installed inside
        // shopify-accelerated-checkout / shop-pay-wallet-button.
        wrap.addEventListener('click', handler, true);
        // Touch flows can dispatch pointerdown before click and trigger the
        // wallet button early - swallow those too.
        wrap.addEventListener(
          'pointerdown',
          (e) => {
            if (!this.slots.length) return;
            const inPaymentBtn = e.target.closest('.shopify-payment-button');
            if (!inPaymentBtn) return;
            // Don't preventDefault here (it would kill the click entirely);
            // just stop bubbling into the wallet's own pointerdown listener.
            e.stopPropagation();
          },
          true
        );
      }

      /* ---------- Back-in-stock signup ---------- */

      // Klaviyo's own "Notify me" only appears on the stock product form,
      // which this builder hides, so sold-out sizes had no way to subscribe.
      // A style that is sold out in the chosen size (but made in it) gets
      // `.is-notify` in renderStyles; tapping it opens the panel rendered by
      // snippets/bundle-builder-a.liquid, which posts to Klaviyo's
      // back-in-stock API for that exact Shopify variant. The "Subscribed to
      // Back in Stock" flow then sends the restock email.
      setupNotify() {
        this.notifyEl = this.querySelector('[data-bb-notify]');
        this.notifyHintEl = this.querySelector('[data-bb-notify-hint]');
        this.notifyTarget = null; // { variantId, title, size, btn }
        this.notifySubscribed = new Set(); // variant IDs signed up on this page view
        if (!this.notifyEl) return;

        this.notifyForm = this.notifyEl.querySelector('[data-bb-notify-form]');
        this.notifyEmail = this.notifyEl.querySelector('[data-bb-notify-email]');
        this.notifySubmit = this.notifyEl.querySelector('[data-bb-notify-submit]');
        this.notifyMsg = this.notifyEl.querySelector('[data-bb-notify-msg]');
        this.notifyVariantEl = this.notifyEl.querySelector('[data-bb-notify-variant]');
        this.notifyThumb = this.notifyEl.querySelector('[data-bb-notify-thumb]');
        this.notifyEl.setAttribute('tabindex', '-1');

        this.notifyForm.addEventListener('submit', (e) => {
          e.preventDefault();
          this.onNotifySubmit();
        });
        this.notifyEl.querySelector('[data-bb-notify-close]').addEventListener('click', () => {
          this.closeNotify(true);
        });
        this.notifyEl.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            this.closeNotify(true);
          }
        });
      }

      // An email we already know, so the shopper only has to press the button:
      // the logged-in customer, then the last email used here, then whoever
      // Klaviyo has identified. klaviyo.identify({}) resolves with the same
      // __kla_id cookie identity (base64 JSON, carries $email once known), so
      // read the cookie directly rather than waiting for klaviyo.js to load.
      knownEmail() {
        const fromPage = this.notifyEl ? (this.notifyEl.dataset.customerEmail || '').trim() : '';
        if (fromPage) return fromPage;
        if (this._notifyLastEmail) return this._notifyLastEmail;
        try {
          const m = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]+)/);
          if (m) {
            const identity = JSON.parse(atob(decodeURIComponent(m[1])));
            if (identity && typeof identity.$email === 'string') return identity.$email;
          }
        } catch (_) {}
        return '';
      }

      openNotify(btn) {
        if (!this.notifyEl) return;
        const prod = this.getStyleDataFromButton(btn);
        const entry = prod.variants[this.sizeCode];
        if (!entry || !entry.id) return;

        this.notifyTarget = { variantId: entry.id, title: prod.title, size: this.sizeCode, btn };
        this.notifyVariantEl.textContent =
          prod.title + ' is sold out in ' + this.sizeCode + '.' + (entry.back ? ' Back in stock around ' + entry.back + '.' : '');
        this.notifyThumb.innerHTML = '';
        if (prod.thumb) {
          const img = document.createElement('img');
          img.src = prod.thumb;
          img.alt = '';
          img.width = 48;
          img.height = 48;
          this.notifyThumb.appendChild(img);
        }

        if (this.notifySubscribed.has(String(entry.id))) {
          this.showNotifyDone(this._notifyLastEmail || '');
        } else {
          this.notifyForm.hidden = false;
          this.setNotifyMsg('', '');
          this.notifyEmail.removeAttribute('aria-invalid');
          if (!this.notifyEmail.value) this.notifyEmail.value = this.knownEmail();
        }

        this.styleButtons.forEach((b) => {
          if (b.classList.contains('is-notify')) {
            b.setAttribute('aria-expanded', b === btn ? 'true' : 'false');
          }
        });
        this.notifyEl.hidden = false;
        // Move focus into the panel so screen readers announce it, without
        // focusing the input (that would pop the phone keyboard on a tap
        // that may only have been a look).
        try {
          this.notifyEl.focus({ preventScroll: true });
        } catch (_) {}
        this.notifyEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }

      closeNotify(returnFocus) {
        if (!this.notifyEl || this.notifyEl.hidden) return;
        const btn = this.notifyTarget && this.notifyTarget.btn;
        this.notifyEl.hidden = true;
        this.notifyTarget = null;
        this.styleButtons.forEach((b) => {
          if (b.classList.contains('is-notify')) b.setAttribute('aria-expanded', 'false');
        });
        if (returnFocus && btn && btn.isConnected) btn.focus();
      }

      setNotifyMsg(text, kind) {
        if (!this.notifyMsg) return;
        this.notifyMsg.textContent = text;
        this.notifyMsg.classList.toggle('is-error', kind === 'error');
        this.notifyMsg.classList.toggle('is-success', kind === 'success');
      }

      showNotifyDone(email) {
        const t = this.notifyTarget;
        this.notifyForm.hidden = true;
        this.setNotifyMsg(
          "You're on the list. We'll email " + (email || 'you') + ' when ' +
            (t ? t.title + ' in ' + t.size : 'it') + ' is back in stock.',
          'success'
        );
      }

      async onNotifySubmit() {
        const target = this.notifyTarget;
        if (!target || this._notifyBusy) return;

        const email = (this.notifyEmail.value || '').trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          this.notifyEmail.setAttribute('aria-invalid', 'true');
          this.setNotifyMsg('Please enter a valid email address.', 'error');
          this.notifyEmail.focus();
          return;
        }
        this.notifyEmail.removeAttribute('aria-invalid');
        this.setNotifyMsg('', '');

        this._notifyBusy = true;
        this.notifySubmit.disabled = true;
        this.notifySubmit.textContent = 'Signing you up…';
        try {
          await this.subscribeBackInStock(email, target);
          this.notifySubscribed.add(String(target.variantId));
          this._notifyLastEmail = email;
          if (this.notifyTarget === target) this.showNotifyDone(email);
        } catch (err) {
          console.error('[bundle-builder-au] back-in-stock signup failed', err);
          if (err && err.field === 'email') this.notifyEmail.setAttribute('aria-invalid', 'true');
          this.setNotifyMsg(
            (err && err.shopperMessage) ||
              "Sorry, we couldn't sign you up just now. Please try again.",
            'error'
          );
        } finally {
          this._notifyBusy = false;
          this.notifySubmit.disabled = false;
          this.notifySubmit.textContent = 'Notify me';
        }
      }

      // Pareto Hub's back-in-stock form, when the Pareto forms script has it.
      paretoBackInStock() {
        const ph = window.__phForms;
        const forms = ph && typeof ph.forms === 'function' ? ph.forms() : [];
        const form = forms.find((f) => f && f.targeting && f.targeting.backInStock);
        if (!form) return null;
        const tag = document.querySelector('script[src*="/storefront/forms.js"]');
        const endpoint = ((tag && tag.dataset.endpoint) || 'https://paretohub.com').replace(/\/$/, '');
        return { formId: form.id, endpoint };
      }

      async subscribeBackInStock(email, target) {
        const pareto = this.paretoBackInStock();
        if (pareto) return this.subscribePareto(pareto, email, target);
        // The Pareto script hasn't loaded its forms yet (or is missing).
        const err = new Error('Pareto back-in-stock form not available');
        err.shopperMessage = "Sorry, we couldn't sign you up just now. Please try again in a moment.";
        throw err;
      }

      // POST /api/forms/submit with the variant: records "Subscribed to Back in
      // Stock" and starts the wait. It doesn't subscribe them to marketing.
      async subscribePareto(pareto, email, target) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', target.variantId);
        const prod = target.btn ? this.getStyleDataFromButton(target.btn) : null;
        const res = await fetch(pareto.endpoint + '/api/forms/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'omit',
          body: JSON.stringify({
            formId: pareto.formId,
            email,
            fields: {},
            pageUrl: window.location.href.slice(0, 2000),
            variantId: String(target.variantId),
            productName: target.title + (target.size ? ' (' + target.size + ')' : ''),
            productUrl: url.toString(),
            productImage: prod && prod.thumb ? new URL(prod.thumb, window.location.origin).toString() : null,
            first: false,
          }),
        });
        let json = null;
        try {
          json = await res.json();
        } catch (_) {}
        if (!res.ok) {
          const err = new Error('Pareto back-in-stock ' + res.status + (json && json.error ? ': ' + json.error : ''));
          if (res.status === 429) err.shopperMessage = 'Lots of sign-ups right now. Please try again in a minute.';
          else if (json && json.error && /email/i.test(json.error)) {
            err.field = 'email';
            err.shopperMessage = 'Please check your email address and try again.';
          }
          throw err;
        }
        // Lets the Pareto script recognise this shopper's later visits.
        try {
          if (json && typeof json.token === 'string' && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(json.token)) {
            localStorage.setItem('ph_v', json.token);
          }
        } catch (_) {}
      }

      /* ---------- Size guide modal ---------- */

      openSizeGuideModal(href) {
        if (!this._sizeGuideModal) {
          const modal = document.createElement('div');
          modal.className = 'bb-modal';
          modal.setAttribute('aria-hidden', 'true');
          modal.innerHTML =
            '<div class="bb-modal__backdrop" data-bb-modal-close></div>' +
            '<div class="bb-modal__dialog" role="dialog" aria-modal="true" aria-label="Size Guide">' +
              '<button type="button" class="bb-modal__close" data-bb-modal-close aria-label="Close size guide">&times;</button>' +
              '<div class="bb-modal__body">' +
                '<div class="bb-modal__loading">Loading size guide…</div>' +
              '</div>' +
            '</div>';
          document.body.appendChild(modal);
          this._sizeGuideModal = modal;

          modal.addEventListener('click', (e) => {
            if (e.target.closest('[data-bb-modal-close]')) this.closeSizeGuideModal();
          });
          this._onEscKey = (e) => {
            if (e.key === 'Escape') this.closeSizeGuideModal();
          };

          // Fetch the size guide page and extract the main content
          fetch(href, { headers: { Accept: 'text/html' } })
            .then((r) => r.text())
            .then((html) => {
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const content =
                doc.querySelector('.page__content') ||
                doc.querySelector('main .rte') ||
                doc.querySelector('main .page') ||
                doc.querySelector('#MainContent') ||
                doc.querySelector('main');
              const body = modal.querySelector('.bb-modal__body');
              if (content) {
                body.innerHTML = '';
                body.appendChild(content);
              } else {
                body.innerHTML =
                  '<p style="text-align:center">Unable to load size guide. ' +
                  '<a href="' + href + '" target="_blank" rel="noopener">Open in a new tab</a></p>';
              }
            })
            .catch(() => {
              const body = modal.querySelector('.bb-modal__body');
              if (body) {
                body.innerHTML =
                  '<p style="text-align:center">Unable to load size guide. ' +
                  '<a href="' + href + '" target="_blank" rel="noopener">Open in a new tab</a></p>';
              }
            });
        }

        this._sizeGuideModal.classList.add('is-open');
        this._sizeGuideModal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('bb-modal-open');
        document.addEventListener('keydown', this._onEscKey);
      }

      closeSizeGuideModal() {
        if (!this._sizeGuideModal) return;
        this._sizeGuideModal.classList.remove('is-open');
        this._sizeGuideModal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('bb-modal-open');
        if (this._onEscKey) document.removeEventListener('keydown', this._onEscKey);
      }

      /* ---------- Utilities ---------- */

      showError(msg) {
        if (!this.errorEl) return;
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }

      formatMoney(cents) {
        const fmt = this.data.moneyFormat || '${{amount}}';
        const dollars = (cents / 100).toFixed(2);
        const withoutDecimals = Math.round(cents / 100).toString();
        return fmt
          .replace(/\{\{\s*amount\s*\}\}/g, dollars)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withoutDecimals)
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, dollars.replace('.', ','))
          .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withoutDecimals);
      }
    }
  );
}
